/**
 * Theme-Modus-Cache (Design-Komponente "Farbschema_Cache").
 *
 * Kapselt den Lese-/Schreibzugriff auf die gewählte Farbschema-Einstellung
 * (`auto` | `light` | `dark`) im Browser-`localStorage`. Der Cache ist nicht
 * die Quelle der Wahrheit, sondern ein Start-Cache/Fallback — das Muster
 * folgt dem Sprach_Cache (`i18n/languageStorage.ts`).
 *
 * Alle `localStorage`-Zugriffe sind in `try/catch` gekapselt, damit die App
 * auch im Private-Mode oder bei fehlendem `localStorage` (z. B. SSR) nicht
 * abbricht: Lesefehler liefern `null`, Schreibfehler sind ein no-op.
 */

/** Mögliche Farbschema-Einstellungen des Nutzers. */
export type ThemeModePreference = "auto" | "light" | "dark";

const STORAGE_KEY = "schoolescort.themeMode";

const SUPPORTED_MODES: ThemeModePreference[] = ["auto", "light", "dark"];

/** Prüft, ob ein Rohwert eine gültige Farbschema-Einstellung benennt. */
function isSupportedMode(value: unknown): value is ThemeModePreference {
  return (
    typeof value === "string" &&
    SUPPORTED_MODES.includes(value as ThemeModePreference)
  );
}

/**
 * Liest den Farbschema-Cache aus dem `localStorage`.
 *
 * Gibt den Wert nur zurück, wenn er gültig ist; andernfalls (fehlender
 * Eintrag, ungültiger Wert oder `localStorage`-Fehler) `null` — der Aufrufer
 * fällt dann auf `auto` (System-Einstellung) zurück.
 */
export function readThemeMode(): ThemeModePreference | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isSupportedMode(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Schreibt die Farbschema-Einstellung in den Cache.
 *
 * Fehler beim `localStorage`-Zugriff werden geschluckt (no-op).
 */
export function writeThemeMode(mode: ThemeModePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // no-op: Farbschema-Cache ist nur ein Fallback, kein harter Fehler.
  }
}
